import React from "react";
import PropTypes from "prop-types";
import { useMatch, useNavigate } from "react-router-dom";
import SFToggleButton from "../../../toggleButton";
import SFTypography from "../../../typography";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import { SFGridContainer, SFGridItem, SFStack } from "../../../grid";
import { SFPCCheckIcon } from "../../../icons";
import getStyled from "../../../../hooks/getStyled";

const { styled } = getStyled();

const PaperStyled = styled(Paper)(({ theme }) => ({
  padding: "20px 20px 40px 20px",
  borderRadius: "0px 0px 4px 4px",
  borderTop: `1px solid ${theme.palette.grey250.main}`,
  borderBottom: `1px solid ${theme.palette.grey250.main}`,
  boxShadow: "0px 3px 9px 0px rgba(183, 183, 183, 0.10)",
}));

const ButtonStyled = styled(SFToggleButton)(({ theme }) => ({
  width: "100%",
  height: "120px",
  padding: "8px 10px",
  textTransform: "none",
  textAlign: "left",
  borderRadius: "4px",
  border: `1px solid ${theme.palette.grey250.main}`,
  backgroundColor: theme.palette.white.main,
  "#icon-wrapper": {
    padding: "10px 0px",
    "& .MuiSvgIcon-root": {
      fontSize: "74px",
      filter: "grayscale(100%)",
    },
  },
  "&:hover, &.Mui-selected, &.Mui-selected:hover": {
    border: `1px solid ${theme.palette.blue300.main}`,
    outline: `1px solid ${theme.palette.blue300.main}`,
    backgroundColor: theme.palette.white.main,
    "#icon-wrapper": {
      "& .MuiSvgIcon-root": {
        filter: "none",
      },
    },
  },
}));

const PCIconCheckWrapper = styled("span")(({ theme }) => ({
  "& .MuiSvgIcon-root": {
    backgroundColor: "transparent !important",
    // color on the icon Component is not being passed so the below is needed.
    fontSize: "21px",
    padding: 0,
    color: theme.palette.blue300.main,
  },
}));

const NavIconWrapper = styled("span")(() => ({
  "& .MuiSvgIcon-root": {
    width: "72px",
    height: "72px",
    backgroundColor: "transparent !important",
  },
}));

const CustomHeadingTypography = styled(SFTypography)(({ theme }) => ({
  fontSize: "11px",
  letterSpacing: "0.5px",
  textTransform: "uppercase",
}));

const NavMenuItem = (props) => {
  const match = useMatch(props.path);

  return (
    <ButtonStyled selected={!!match} onClick={props.onClick}>
      <SFStack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1.25}
        width="100%"
      >
        <SFStack direction="row" spacing={2}>
          <NavIconWrapper id="icon-wrapper">{props.icon}</NavIconWrapper>
          <SFStack
            direction="column"
            justifyContent="space-between"
            sx={{ padding: "10px 0px" }}
          >
            <SFStack direction="column" spacing={0.5} alignItems="flex-start">
              <CustomHeadingTypography variant="rightColumnCard">
                {props.header}
              </CustomHeadingTypography>
              <SFTypography variant="body2">{props.description}</SFTypography>
            </SFStack>
            {props.highlight}
          </SFStack>
        </SFStack>
        {props.default ? (
          <PCIconCheckWrapper>
            <SFPCCheckIcon />
          </PCIconCheckWrapper>
        ) : (
          <span style={{ width: "34px" }} />
        )}
      </SFStack>
    </ButtonStyled>
  );
};

NavMenuItem.propTypes = {
  // The icon to display in the button
  icon: PropTypes.element,
  // The header text to display in the button
  header: PropTypes.string,
  // The description text to display in the button
  description: PropTypes.string,
  // An element to display below the description of the nav item
  highlight: PropTypes.element,
  // The function to call when the button is clicked
  onClick: PropTypes.func,
  // Whether the button is the default (renders a check icon)
  default: PropTypes.bool,
  // The path associated with the button
  path: PropTypes.string,
};

NavMenuItem.defaultProps = {
  header: "N/A",
  description: "N/A",
  default: false,
};

const NavButtonMenu = ({ navDefs = [], anchorEl, open, onClose }) => {
  const navigate = useNavigate();

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      role={undefined}
      placement="bottom-start"
      transition
      disablePortal
      sx={{ zIndex: 1000 }}
    >
      {({ TransitionProps, placement }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin:
              placement === "bottom-start" ? "left top" : "left bottom",
          }}
        >
          <PaperStyled sx={{ width: anchorEl.offsetWidth }}>
            <SFGridContainer spacing={4}>
              {navDefs.map((navDef, index) => (
                <SFGridItem key={index} sm={12} md={6} lg={4} xl={3}>
                  <NavMenuItem
                    icon={navDef.icon}
                    header={navDef.header}
                    description={navDef.description}
                    highlight={navDef.highlight}
                    onClick={() => {
                      onClose();
                      navigate(navDef.path);
                    }}
                    default={navDef.default}
                    path={navDef.path}
                  />
                </SFGridItem>
              ))}
            </SFGridContainer>
          </PaperStyled>
        </Grow>
      )}
    </Popper>
  );
};

export default NavButtonMenu;

NavButtonMenu.propTypes = {
  // The definitions for the navigation buttons
  navDefs: PropTypes.arrayOf(
    PropTypes.shape({
      // The icon to display in the button
      icon: PropTypes.element,
      // The header text to display in the button
      header: PropTypes.string,
      // The description text to display in the button
      description: PropTypes.string,
      // The path to navigate to when the button is clicked
      path: PropTypes.string,
      // Whether the button is the default (renders a check icon)
      default: PropTypes.bool,
      // An element to display below the description of the nav item
      highlight: PropTypes.element,
    })
  ),
  // The element to anchor the menu to
  anchorEl: PropTypes.object,
  // Whether the menu is open
  open: PropTypes.bool,
  // The function to call when the menu is closed
  onClose: PropTypes.func,
};
